/* global Utility */
/**
 * Compare table: syncs localStorage handles to the cart only long enough to fetch section HTML,
 * then clears `_compare-handles` on the cart. Removing a product updates storage and the table DOM
 * without a section refetch.
 *
 * On fine-pointer / hover devices, `.compare-table-scroll` supports click-drag horizontal scrolling;
 * touch devices keep native overflow scrolling.
 *
 * Depends on `utility.js` loading first (same `defer` order as `snippets/script-tags.liquid`).
 */

const COMPARE_CART_ATTRIBUTE = '_compare-handles';
const STORAGE_PRIMARY_KEY = 'theme:compare:handles';
const LEGACY_STORAGE_KEYS = ['_compare-handles', 'compare'];
const LEGACY_HANDLE_DELIMITER = '|||';
/** Movement past this threshold (px) turns the gesture into horizontal scroll drag (non-touch). */
const MOUSE_DRAG_SCROLL_THRESHOLD_PX = 5;

/**
 * @param {unknown} raw
 * @returns {string}
 */
const normalizeHandlesCsv = (raw) => {
  if (raw == null || raw === '') {
    return '';
  }

  return String(raw)
    .split(',')
    .map((handle) => handle.trim())
    .filter(Boolean)
    .join(',');
};

/**
 * @param {string} primaryRaw
 * @returns {string[]|null}
 */
const parseThemeStorageHandles = (primaryRaw) => {
  if (!primaryRaw) {
    return null;
  }

  try {
    const parsed = JSON.parse(primaryRaw);
    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.value)) {
      return parsed.value;
    }
  } catch {
    // Non-JSON value; fall through to legacy keys.
  }

  return null;
};

/**
 * @returns {string}
 */
const readLegacyHandlesCsv = () => {
  for (const key of LEGACY_STORAGE_KEYS) {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      continue;
    }

    return normalizeHandlesCsv(
      raw
        .split(LEGACY_HANDLE_DELIMITER)
        .map((h) => h.trim())
        .filter(Boolean)
        .join(','),
    );
  }

  return '';
};

class CompareTable extends HTMLElement {
  /** @type {boolean} */
  #initialized = false;

  /** @type {(event: MouseEvent) => void} */
  #boundOnHostClick = this.#onHostClick.bind(this);

  /** @type {(event: PointerEvent) => void} */
  #boundPointerDown = this.#onPointerDown.bind(this);

  /** @type {(event: PointerEvent) => void} */
  #boundPointerMove = this.#onPointerMove.bind(this);

  /** @type {(event: PointerEvent) => void} */
  #boundPointerEnd = this.#onPointerEnd.bind(this);

  /** @type {AddEventListenerOptions} */
  #pointerMoveOptions = { passive: false };

  /** @type {HTMLElement|null} */
  #dragScrollEl = null;

  /** @type {number} */
  #dragStartClientX = 0;

  /** @type {number} */
  #dragStartScrollLeft = 0;

  /** @type {boolean} */
  #dragScrollActive = false;

  /** @type {number} */
  #activeDragPointerId = -1;

  /** @type {((event: MouseEvent) => void) | null} */
  #pendingClickSuppressor = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', this.#boundOnHostClick);
    this.addEventListener('pointerdown', this.#boundPointerDown);

    if (this.#initialized) {
      return;
    }
    this.#initialized = true;

    void this.#syncAndRenderWhenReady();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#boundOnHostClick);
    this.removeEventListener('pointerdown', this.#boundPointerDown);
    this.#teardownMouseDragScroll();
    this.#initialized = false;
  }

  /**
   * Desktop / fine pointer: enable click-drag horizontal scroll. Touch keeps native overflow scrolling.
   *
   * @returns {boolean}
   */
  #shouldEnableMouseDragScroll() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  /**
   * @returns {void}
   */
  #teardownMouseDragScroll() {
    if (this.#pendingClickSuppressor) {
      document.removeEventListener('click', this.#pendingClickSuppressor, true);
      this.#pendingClickSuppressor = null;
    }

    document.removeEventListener('pointermove', this.#boundPointerMove, this.#pointerMoveOptions);
    document.removeEventListener('pointerup', this.#boundPointerEnd);
    document.removeEventListener('pointercancel', this.#boundPointerEnd);

    if (this.#dragScrollEl) {
      this.#dragScrollEl.removeAttribute('data-dragging');

      try {
        this.#dragScrollEl.releasePointerCapture(this.#activeDragPointerId);
      } catch {
        // ignore
      }
    }

    this.#dragScrollEl = null;
    this.#dragScrollActive = false;
    this.#activeDragPointerId = -1;
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  #onPointerDown(event) {
    if (!this.#shouldEnableMouseDragScroll()) {
      return;
    }

    if (event.pointerType === 'touch') {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    if (this.getAttribute('aria-busy') === 'true') {
      return;
    }

    const scrollEl = event.target?.closest?.('.compare-table-scroll');

    if (!scrollEl || !this.contains(scrollEl)) {
      return;
    }

    if (scrollEl.scrollWidth <= scrollEl.clientWidth) {
      return;
    }

    this.#dragScrollEl = scrollEl;
    this.#dragStartClientX = event.clientX;
    this.#dragStartScrollLeft = scrollEl.scrollLeft;
    this.#dragScrollActive = false;
    this.#activeDragPointerId = event.pointerId;

    document.addEventListener('pointermove', this.#boundPointerMove, this.#pointerMoveOptions);
    document.addEventListener('pointerup', this.#boundPointerEnd);
    document.addEventListener('pointercancel', this.#boundPointerEnd);
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  #onPointerMove(event) {
    if (!this.#dragScrollEl || event.pointerId !== this.#activeDragPointerId) {
      return;
    }

    const deltaX = event.clientX - this.#dragStartClientX;

    if (!this.#dragScrollActive) {
      if (Math.abs(deltaX) <= MOUSE_DRAG_SCROLL_THRESHOLD_PX) {
        return;
      }

      this.#dragScrollActive = true;
      this.#dragScrollEl.setAttribute('data-dragging', '');

      try {
        this.#dragScrollEl.setPointerCapture(this.#activeDragPointerId);
      } catch {
        // ignore
      }
    }

    event.preventDefault();
    this.#dragScrollEl.scrollLeft = this.#dragStartScrollLeft - deltaX;
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  #onPointerEnd(event) {
    if (!this.#dragScrollEl || event.pointerId !== this.#activeDragPointerId) {
      return;
    }

    document.removeEventListener('pointermove', this.#boundPointerMove, this.#pointerMoveOptions);
    document.removeEventListener('pointerup', this.#boundPointerEnd);
    document.removeEventListener('pointercancel', this.#boundPointerEnd);

    const scrollEl = this.#dragScrollEl;
    const didDrag = this.#dragScrollActive;

    try {
      scrollEl.releasePointerCapture(this.#activeDragPointerId);
    } catch {
      // ignore
    }

    scrollEl.removeAttribute('data-dragging');

    this.#dragScrollEl = null;
    this.#dragScrollActive = false;
    this.#activeDragPointerId = -1;

    if (!didDrag) {
      return;
    }

    if (this.#pendingClickSuppressor) {
      document.removeEventListener('click', this.#pendingClickSuppressor, true);
      this.#pendingClickSuppressor = null;
    }

    const suppressDragClick = (clickEvent) => {
      if (!this.contains(clickEvent.target)) {
        return;
      }

      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      document.removeEventListener('click', suppressDragClick, true);
      this.#pendingClickSuppressor = null;
    };

    this.#pendingClickSuppressor = suppressDragClick;
    document.addEventListener('click', suppressDragClick, true);
  }

  /**
   * @param {MouseEvent} event
   * @returns {void}
   */
  #onHostClick(event) {
    if (this.getAttribute('aria-busy') === 'true') {
      return;
    }

    const trigger = event.target?.closest?.('[data-remove-from-comparison]');

    if (!trigger || !this.contains(trigger)) {
      return;
    }

    if (typeof Utility !== 'undefined' && Utility.DOM?.isPrimaryUnmodifiedClick) {
      if (!Utility.DOM.isPrimaryUnmodifiedClick(event)) {
        return;
      }
    }

    const handle = (trigger.dataset.productHandle || '').trim();

    if (!handle) {
      return;
    }

    event.preventDefault();
    this.#removeComparedProduct(handle, trigger);
  }

  /**
   * @param {string} handle
   * @param {Element} trigger
   * @returns {void}
   */
  #removeComparedProduct(handle, trigger) {
    if (typeof Utility === 'undefined') {
      return;
    }

    const csv = normalizeHandlesCsv(this.#readDesiredHandlesFromStorage());
    const order = csv.split(',').filter(Boolean);
    const next = order.filter((h) => h !== handle);

    if (next.length === order.length) {
      return;
    }

    this.#persistHandleArrayToStorage(next);
    window.dispatchEvent(new CustomEvent('theme:compare:change'));

    const title = (trigger.dataset.productTitle || '').trim();
    const announce = (this.dataset.announceRemoved || '').trim();
    const message =
      title && announce
        ? `${announce}: ${title}`
        : announce || title || '';

    if (message) {
      Utility.announceToScreenReader(message, 'polite');
    }

    if (next.length === 0) {
      this.#replaceWithEmptyState();
      void this.#ensureCompareCartAttributeCleared();
      return;
    }

    const table = this.querySelector('.compare-table-scroll table');

    if (table) {
      this.#removeProductColumnFromTable(table, handle);
    }

    void this.#ensureCompareCartAttributeCleared();
  }

  /**
   * @returns {void}
   */
  #replaceWithEmptyState() {
    const msg = (this.dataset.emptyStateMessage || '').trim();
    const wrap = document.createElement('div');
    wrap.className = 'compare-table-empty py-10';
    wrap.setAttribute('role', 'status');
    const paragraph = document.createElement('p');
    paragraph.className = 'text-center';
    paragraph.textContent = msg;
    wrap.appendChild(paragraph);
    this.replaceChildren(wrap);
  }

  /**
   * @param {HTMLTableElement} table
   * @param {string} handle
   * @returns {void}
   */
  #removeProductColumnFromTable(table, handle) {
    const productRows = table.querySelectorAll('tr.compare-table-row--product');

    for (const row of productRows) {
      const button = row.querySelector('[data-remove-from-comparison]');
      const rowHandle = (button?.dataset.productHandle || '').trim();

      if (rowHandle === handle) {
        row.remove();
        return;
      }
    }
  }

  /**
   * @param {string[]} handlesArray
   * @returns {void}
   */
  #persistHandleArrayToStorage(handlesArray) {
    if (typeof Utility !== 'undefined' && Utility.Storage?.Local?.set) {
      Utility.Storage.Local.set(STORAGE_PRIMARY_KEY, handlesArray);
      return;
    }

    try {
      window.localStorage.setItem(STORAGE_PRIMARY_KEY, JSON.stringify(handlesArray));
    } catch {
      // Quota / private mode — ignore
    }
  }

  /**
   * Waits for `utility.js` (global `Utility`) so storage helpers match `theme-compare.js`.
   *
   * @returns {Promise<void>}
   */
  async #syncAndRenderWhenReady() {
    let attempts = 0;
    const maxAttempts = 20;

    while (typeof Utility === 'undefined' && attempts < maxAttempts) {
      await new Promise((resolve) => {
        requestAnimationFrame(resolve);
      });
      attempts += 1;
    }

    await this.#syncAndRender();
  }

  /**
   * @returns {Promise<void>}
   */
  async #syncAndRender() {
    const sectionId = this.dataset.sectionId;

    if (!sectionId) {
      console.warn('[CompareTable] Missing data-section-id.');
      return;
    }

    if (typeof Utility === 'undefined') {
      console.error('[CompareTable] Utility is not loaded.');
      return;
    }

    const desiredCsv = normalizeHandlesCsv(this.#readDesiredHandlesFromStorage());

    if (desiredCsv === '') {
      this.#replaceWithEmptyState();
      await this.#ensureCompareCartAttributeCleared();
      return;
    }

    if (this.#domMatchesHandles(desiredCsv)) {
      await this.#ensureCompareCartAttributeCleared();
      return;
    }

    this.setAttribute('aria-busy', 'true');

    try {
      const cartLike = await this.#persistHandlesToCart(desiredCsv);
      this.dataset.initialCartHandles = desiredCsv;
      Utility.CartUi.syncHeaderFromCartLike(cartLike);

      const html = await this.#fetchSectionHtml(sectionId);
      const nextInnerHtml = this.#extractCompareTableInnerHtml(html, sectionId);

      if (nextInnerHtml == null) {
        throw new Error('Compare table markup was not found in the section response.');
      }

      this.innerHTML = nextInnerHtml;

      if (normalizeHandlesCsv(this.#readDesiredHandlesFromStorage()) === '') {
        this.#replaceWithEmptyState();
      }

      const clearedCart = await this.#persistHandlesToCart('');
      this.dataset.initialCartHandles = '';
      Utility.CartUi.syncHeaderFromCartLike(clearedCart);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('[CompareTable]', err);

      const fallbackMessage =
        (this.dataset.loadError || '').trim() || 'Could not load the comparison table.';
      Utility.announceToScreenReader(fallbackMessage, 'assertive');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Clears `_compare-handles` on the cart when it still holds a value (SSR or legacy).
   *
   * @returns {Promise<void>}
   */
  async #ensureCompareCartAttributeCleared() {
    if (!normalizeHandlesCsv(this.dataset.initialCartHandles ?? '')) {
      return;
    }

    try {
      const cartLike = await this.#persistHandlesToCart('');
      this.dataset.initialCartHandles = '';
      Utility.CartUi.syncHeaderFromCartLike(cartLike);
    } catch (error) {
      console.warn('[CompareTable] Could not clear compare cart attribute.', error);
    }
  }

  /**
   * Mirrors `theme-compare.js` / `Utility.Storage.Local` plus raw-key fallbacks.
   *
   * @returns {string}
   */
  #readDesiredHandlesFromStorage() {
    /** @type {unknown[]} */
    let handles = [];

    if (typeof Utility !== 'undefined' && Utility.Storage?.Local?.get) {
      const fromUtility = Utility.Storage.Local.get(STORAGE_PRIMARY_KEY, []);
      if (Array.isArray(fromUtility) && fromUtility.length > 0) {
        handles = fromUtility;
      }
    }

    if (handles.length === 0) {
      const primaryRaw = window.localStorage.getItem(STORAGE_PRIMARY_KEY);
      const fromTheme = parseThemeStorageHandles(primaryRaw);

      if (fromTheme?.length) {
        handles = fromTheme;
      } else if (primaryRaw) {
        try {
          const parsed = JSON.parse(primaryRaw);
          if (Array.isArray(parsed)) {
            handles = parsed;
          }
        } catch {
          // ignore
        }
      }
    }

    if (handles.length === 0) {
      return readLegacyHandlesCsv();
    }

    return normalizeHandlesCsv(
      handles
        .filter((v) => typeof v === 'string' && v.trim())
        .map((v) => v.trim())
        .join(','),
    );
  }

  /**
   * @param {HTMLTableElement} table
   * @returns {string[]}
   */
  #readHandlesFromProductRow(table) {
    const rows = table.querySelectorAll('tr.compare-table-row--product');

    return Array.from(rows)
      .map((row) => {
        const button = row.querySelector('[data-remove-from-comparison]');
        return (button?.dataset.productHandle || '').trim();
      })
      .filter(Boolean);
  }

  /**
   * @param {string} csv
   * @returns {string}
   */
  #sortedHandlesKey(csv) {
    return normalizeHandlesCsv(csv)
      .split(',')
      .filter(Boolean)
      .sort()
      .join('\u0001');
  }

  /**
   * @param {string} desiredCsv
   * @returns {boolean}
   */
  #domMatchesHandles(desiredCsv) {
    const want = normalizeHandlesCsv(desiredCsv);

    if (want === '') {
      return this.#hasFinalEmptyState();
    }

    const table = this.querySelector('.compare-table-scroll table');
    if (!table) {
      return false;
    }

    const got = normalizeHandlesCsv(this.#readHandlesFromProductRow(table).join(','));

    if (got === '') {
      return false;
    }

    return this.#sortedHandlesKey(got) === this.#sortedHandlesKey(want);
  }

  /**
   * Final “no products” message (not the initial loading placeholder).
   *
   * @returns {boolean}
   */
  #hasFinalEmptyState() {
    return Boolean(this.querySelector('.compare-table-empty'));
  }

  /**
   * @param {string} handlesCsv
   * @returns {Promise<Record<string, unknown>>}
   */
  async #persistHandlesToCart(handlesCsv) {
    const root = window.Shopify?.routes?.root ?? '/';
    const url = `${root}cart/update.js`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [COMPARE_CART_ATTRIBUTE]: handlesCsv || '',
        },
      }),
    });

    if (!response.ok) {
      let detail = '';
      try {
        detail = await response.text();
      } catch {
        // ignore
      }
      throw new Error(detail.trim() || `Cart update failed (${response.status}).`);
    }

    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  /**
   * @param {string} sectionId
   * @returns {Promise<string>}
   */
  async #fetchSectionHtml(sectionId) {
    const url = new URL(window.location.href);

    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) {
      throw new Error(`Section request failed (${response.status}).`);
    }

    return await response.text();
  }

  /**
   * @param {string} html
   * @param {string} sectionId
   * @returns {string|null}
   */
  #extractCompareTableInnerHtml(html, sectionId) {
    const doc = Utility.DOM.parse(html);
    const selector = `compare-table[data-section-id="${CSS.escape(sectionId)}"]`;
    const node = doc.querySelector(selector);

    return node?.innerHTML ?? null;
  }
}

customElements.define('compare-table', CompareTable);
